/**
 * arrastar — onde a janelinha da ligação fica depois de um arrasto (Task 11, 24/10/2026).
 *
 * Função pura: recebe onde a janela estava (`atual`, o canto de cima à esquerda), quanto o ponteiro
 * andou (`delta`), o tamanho da janela e o da tela, e devolve onde ela fica — SEMPRE dentro da tela e com
 * `margem` de folga nas quatro bordas. Quem chama passa o `delta` TOTAL desde o início do arrasto e a posição
 * de ONDE ELE COMEÇOU: assim a janela "volta" junto com o ponteiro depois de bater numa borda, em vez de
 * ficar presa com o que o ponteiro andou a mais.
 *
 * Tela menor que a janela mais as margens (janela de navegador minúscula): fica na margem, nunca negativa.
 *
 * A9 (25/10/2026, WCAG 2.5.7): mudar de lugar SEM arrastar. Além do arrasto, a janela tem 3 cantos num ciclo
 * (`proximoCanto` / `posicaoDoCanto`), o passo das setas do teclado e o limiar que separa um clique de um
 * arrasto. O canto de cima à direita fica FORA do ciclo: é onde moram a pilha de avisos de chamada, o X do
 * painel de thread e o X do painel de pesquisa.
 */

export interface Ponto {
  x: number;
  y: number;
}

export interface Deslocamento {
  dx: number;
  dy: number;
}

export interface Tamanho {
  w: number;
  h: number;
}

/** Prende `valor` entre a margem e `espaco − janela − margem` (nunca abaixo da margem). */
function prender(valor: number, espaco: number, janela: number, margem: number): number {
  const maximo = Math.max(margem, espaco - janela - margem);
  return Math.min(Math.max(valor, margem), maximo);
}

export function posicaoArrastada(
  atual: Ponto,
  delta: Deslocamento,
  janela: Tamanho,
  tela: Tamanho,
  margem = 12
): Ponto {
  return {
    x: prender(atual.x + delta.dx, tela.w, janela.w, margem),
    y: prender(atual.y + delta.dy, tela.h, janela.h, margem),
  };
}

/** Quanto uma seta do teclado move a janela (Shift + seta vai até a borda). */
export const PASSO_DO_TECLADO = 24;

/** Quanto o ponteiro precisa andar desde o `pointerdown` para o gesto deixar de ser um clique e virar arrasto. */
export const LIMIAR_DO_ARRASTO = 4;

/** Os 3 cantos do ciclo: 0 embaixo à direita (onde a janela nasce), 1 embaixo à esquerda, 2 em cima à esquerda. */
export type Canto = 0 | 1 | 2;

export function proximoCanto(canto: Canto): Canto {
  return ((canto + 1) % 3) as Canto;
}

/* As folgas dos cantos, as mesmas das classes do desenho: `right-4`/`left-4` (16), `bottom-24` (96: acima do
   composer de uma linha) e `top-20` (80: abaixo do cabeçalho do app). */
const LADO = 16;
const EMBAIXO = 96;
const TOPO = 80;

/** O canto de cima à esquerda da janela quando ela está no `canto`, sempre presa à tela com a `margem`. */
export function posicaoDoCanto(canto: Canto, janela: Tamanho, tela: Tamanho, margem = 12): Ponto {
  const x = canto === 0 ? tela.w - janela.w - LADO : LADO;
  const y = canto === 2 ? TOPO : tela.h - janela.h - EMBAIXO;
  return {
    x: prender(x, tela.w, janela.w, margem),
    y: prender(y, tela.h, janela.h, margem),
  };
}
